import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export const LIFECYCLE_STAGES = [
  "uploaded",
  "queued",
  "processing",
  "completed",
  "failed",
] as const;

export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number];

export type StatusBadgeTokens = {
  label: "Caricato" | "In Coda" | "In Elaborazione" | "Completato" | "Fallito";
  helper: string;
  glyph: "UP" | "Q" | "PR" | "OK" | "!";
  shape: "rounded" | "pill" | "diamond" | "square";
  styleToken: string;
};

export const STATUS_BADGE_TOKENS: Record<LifecycleStage, StatusBadgeTokens> = {
  uploaded: {
    label: "Caricato",
    helper: "File accettato e in attesa di posizionamento in coda.",
    glyph: "UP",
    shape: "rounded",
    styleToken: "status-chip status-chip--uploaded",
  },
  queued: {
    label: "In Coda",
    helper: "In coda per l'elaborazione.",
    glyph: "Q",
    shape: "pill",
    styleToken: "status-chip status-chip--queued",
  },
  processing: {
    label: "In Elaborazione",
    helper: "Estrazione del testo e analisi del documento in corso.",
    glyph: "PR",
    shape: "diamond",
    styleToken: "status-chip status-chip--processing",
  },
  completed: {
    label: "Completato",
    helper: "Analisi terminata e risultati pronti.",
    glyph: "OK",
    shape: "square",
    styleToken: "status-chip status-chip--completed",
  },
  failed: {
    label: "Fallito",
    helper: "Elaborazione fallita.",
    glyph: "!",
    shape: "square",
    styleToken: "status-chip status-chip--failed",
  },
};

export type StatusBadgeProps = {
  stage: LifecycleStage;
  compact?: boolean;
  tone?: "default" | "warning";
};

export function StatusBadge({
  stage,
  compact = false,
  tone = "default",
}: StatusBadgeProps) {
  const tokens = STATUS_BADGE_TOKENS[stage];
  const isWarningCompleted = stage === "completed" && tone === "warning";

  return (
    <Badge
      className={cn(
        tokens.styleToken,
        isWarningCompleted ? "status-chip--completed-warning" : "",
        compact ? "status-chip--compact px-1.5 py-0.75" : "px-2 py-1",
      )}
      data-shape={tokens.shape}
      data-stage={stage}
      data-tone={tone}
      variant="outline"
    >
      <span className="status-chip__glyph" aria-hidden="true">
        {tokens.glyph}
      </span>
      <span className="status-chip__label">{tokens.label}</span>
    </Badge>
  );
}
